import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SupportNavbar from "../../components/support-components/SupportNavbar";
import SupportTopbar from "../../components/support-components/SupportTopbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "./SupportTicketDetail.css";

function SupportTicketDetail() {
  const navigate = useNavigate();
  const { ticketId } = useParams();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState("Open");
  const [messageType, setMessageType] = useState("internal_note");
  const [message, setMessage] = useState("");
  const [screenshots, setScreenshots] = useState([]);

  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [openingAttachmentId, setOpeningAttachmentId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const allowedScreenshotTypes = new Set([
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
  ]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load support ticket");
      }

      setTicket(data.ticket || null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
      setStatus(data.ticket?.status || "Open");
    } catch (err) {
      setError(err.message || "Failed to load support ticket");
      setTicket(null);
      setMessages([]);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  const attachmentsByMessageId = useMemo(() => {
    return attachments.reduce((acc, attachment) => {
      const key = attachment.support_ticket_message_id || "ticket";

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(attachment);
      return acc;
    }, {});
  }, [attachments]);

  const customerMessages = useMemo(() => {
    return messages.filter((item) => item.is_customer_visible === 1 || item.is_customer_visible === true);
  }, [messages]);

  const internalMessages = useMemo(() => {
    return messages.filter((item) => item.is_customer_visible !== 1 && item.is_customer_visible !== true);
  }, [messages]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const formatFileSize = (value) => {
    const size = Number(value || 0);

    if (!size) return "—";

    if (size < 1024 * 1024) {
      return `${Math.ceil(size / 1024)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  const handleScreenshotChange = (event) => {
    setError("");

    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length > 5) {
      setError("You can upload up to 5 screenshots.");
      event.target.value = "";
      setScreenshots([]);
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) => !allowedScreenshotTypes.has(file.type)
    );

    if (invalidFile) {
      setError("Only PNG, JPG, JPEG, and WEBP screenshots are allowed.");
      event.target.value = "";
      setScreenshots([]);
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > 10 * 1024 * 1024
    );

    if (oversizedFile) {
      setError("Each screenshot must be 10 MB or smaller.");
      event.target.value = "";
      setScreenshots([]);
      return;
    }

    setScreenshots(selectedFiles);
  };

  const handleOpenAttachment = async (attachmentId) => {
    try {
      setOpeningAttachmentId(attachmentId);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/support-tickets/attachments/${attachmentId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

        if (!res.ok) {
            let message = "Failed to open screenshot";

            try {
                const data = await res.json();
                message = data.error || data.message || message;
            } catch {
                // Response may not be JSON.
            }

            throw new Error(message);
        }

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        window.open(blobUrl, "_blank", "noopener,noreferrer");

        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
        }, 60000);
    } catch (err) {
      setError(err.message || "Failed to open screenshot");
    } finally {
      setOpeningAttachmentId(null);
    }
  };

  const renderAttachments = (messageId) => {
    const messageAttachments = attachmentsByMessageId[messageId] || [];

    if (messageAttachments.length === 0) {
      return null;
    }

    return (
      <div className="support-attachment-list">
        {messageAttachments.map((attachment) => (
          <button
            key={attachment.id}
            type="button"
            className="support-attachment-item"
            onClick={() => handleOpenAttachment(attachment.id)}
            disabled={openingAttachmentId === attachment.id}
          >
            <span>
              {attachment.original_file_name || "Screenshot"}
            </span>
            <strong>
              {openingAttachmentId === attachment.id
                ? "Opening..."
                : formatFileSize(attachment.file_size_bytes)}
            </strong>
          </button>
        ))}
      </div>
    );
  };

  const handleStatusSave = async () => {
    try {
      setSavingStatus(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/${ticketId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to update ticket status");
      }

      setSuccess("Ticket status updated.");
      await loadTicket();
    } catch (err) {
      setError(err.message || "Failed to update ticket status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleMessageSubmit = async (event) => {
    event.preventDefault();

    try {
      setSendingMessage(true);
      setError("");
      setSuccess("");

      if (!message.trim()) {
        setError("Message is required.");
        return;
      }

      const token = localStorage.getItem("token");
      const payload = new FormData();

      payload.append("message_type", messageType);
      payload.append("message", message);

      screenshots.forEach((file) => {
        payload.append("screenshots", file);
      });

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/${ticketId}/messages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to add message");
      }

      setMessage("");
      setScreenshots([]);

      const screenshotInput = document.getElementById("support-detail-screenshots");
      if (screenshotInput) {
        screenshotInput.value = "";
      }

      setSuccess(data.message || "Message added.");
      await loadTicket();
    } catch (err) {
      setError(err.message || "Failed to add message");
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <>
      <SupportNavbar />
      <SupportTopbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM Support</p>
            <h1 className="sales-title">
              {ticket ? `Ticket #${ticket.id}` : "Support Ticket"}
            </h1>
            <p className="sales-subtitle">
              {ticket?.subject || "Review ticket details, internal notes, replies, and status."}
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() => navigate("/support-admin/tickets")}
            >
              Back to Tickets
            </button>
          </div>
        </div>

        {error && (
          <div className="support-detail-message error">
            {error}
          </div>
        )}

        {success && (
          <div className="support-detail-message success">
            {success}
          </div>
        )}

        {loading ? (
          <div className="sales-section-card">Loading support ticket...</div>
        ) : !ticket ? (
          <div className="sales-section-card">Support ticket not found.</div>
        ) : (
          <div className="support-detail-layout">
            <div className="support-detail-main">
              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>{ticket.subject}</h2>
                    <p>
                      Submitted by {ticket.submitted_by_name || ticket.submitted_by_email || "Unknown User"}
                    </p>
                  </div>

                  <span className={statusClass(ticket.status)}>
                    {ticket.status}
                  </span>
                </div>

                <div className="support-detail-body">
                  <div className="support-original-message">
                    {ticket.message}
                  </div>

                  {renderAttachments("ticket")}
                </div>
              </div>

              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>Customer Conversation</h2>
                    <p>Customer-visible messages and support replies.</p>
                  </div>
                </div>

                <div className="support-message-list">
                  {customerMessages.length === 0 ? (
                    <div className="support-empty-message">
                      No customer-visible messages yet.
                    </div>
                  ) : (
                    customerMessages.map((item) => (
                      <div key={item.id} className="support-thread-message">
                        <div className="support-thread-meta">
                          <strong>{item.sender_name || item.sender_email || "Unknown User"}</strong>
                          <span>{item.message_type}</span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                        <p>{item.message}</p>
                        {renderAttachments(item.id)}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>Internal Notes</h2>
                    <p>Only platform support users can see these notes.</p>
                  </div>
                </div>

                <div className="support-message-list">
                  {internalMessages.length === 0 ? (
                    <div className="support-empty-message">
                      No internal notes yet.
                    </div>
                  ) : (
                    internalMessages.map((item) => (
                      <div key={item.id} className="support-thread-message internal">
                        <div className="support-thread-meta">
                          <strong>{item.sender_name || item.sender_email || "System"}</strong>
                          <span>{item.message_type}</span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                        <p>{item.message}</p>
                        {renderAttachments(item.id)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <aside className="support-detail-sidebar">
              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>Ticket Status</h2>
                    <p>Update the current support state.</p>
                  </div>
                </div>

                <div className="support-detail-body">
                  <label className="support-detail-field">
                    Status
                    <select
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Waiting on Customer">Waiting on Customer</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </label>

                  <button
                    className="sales-primary-btn support-detail-full-btn"
                    onClick={handleStatusSave}
                    disabled={savingStatus}
                  >
                    {savingStatus ? "Saving..." : "Save Status"}
                  </button>
                </div>
              </div>

              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>Add Message</h2>
                    <p>Save an internal note or reply to the customer.</p>
                  </div>
                </div>

                <form onSubmit={handleMessageSubmit} className="support-detail-body">
                  <label className="support-detail-field">
                    Message Type
                    <select
                      value={messageType}
                      onChange={(event) => setMessageType(event.target.value)}
                    >
                      <option value="internal_note">Internal Note</option>
                      <option value="support_reply">Customer Reply + Email</option>
                    </select>
                  </label>

                  <label className="support-detail-field">
                    Message
                    <textarea
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      rows="8"
                      placeholder={
                        messageType === "support_reply"
                          ? "Write a customer-facing reply..."
                          : "Write an internal note..."
                      }
                    />
                  </label>

                  <label className="support-detail-field">
                    Screenshots
                    <input
                      id="support-detail-screenshots"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      multiple
                      onChange={handleScreenshotChange}
                    />
                    <span className="support-detail-help-text">
                      Optional. Upload up to 5 screenshots.
                    </span>
                  </label>

                  {screenshots.length > 0 && (
                    <div className="support-detail-selected-files">
                      {screenshots.map((file) => (
                        <div key={`${file.name}-${file.size}`}>
                          <span>{file.name}</span>
                          <strong>{formatFileSize(file.size)}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="sales-primary-btn support-detail-full-btn"
                    disabled={sendingMessage}
                  >
                    {sendingMessage ? "Saving..." : "Save Message"}
                  </button>
                </form>
              </div>

              <div className="sales-table-card support-detail-card">
                <div className="sales-table-header">
                  <div>
                    <h2>Ticket Info</h2>
                  </div>
                </div>

                <div className="support-detail-info-list">
                  <div>
                    <span>Category</span>
                    <strong>{ticket.category || "—"}</strong>
                  </div>

                  <div>
                    <span>Priority</span>
                    <strong>{ticket.priority || "—"}</strong>
                  </div>

                  <div>
                    <span>Submitted By</span>
                    <strong>{ticket.submitted_by_name || ticket.submitted_by_email || "—"}</strong>
                  </div>

                  <div>
                    <span>Email</span>
                    <strong>{ticket.submitted_by_email || "—"}</strong>
                  </div>

                  <div>
                    <span>Tenant</span>
                    <strong>{ticket.tenant_type} #{ticket.tenant_id}</strong>
                  </div>

                  <div>
                    <span>Created</span>
                    <strong>{formatDate(ticket.created_at)}</strong>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </>
  );
}

export default SupportTicketDetail;